// Dibuja una tabla simple (título, subtítulo, encabezado, filas, pie) en un
// canvas a 2× con fondo blanco, para copiarla al portapapeles como imagen o
// descargarla — el mismo mecanismo de `construirCanvas` en ListaPrecios.jsx
// (§6.5 de docs/ESTANDAR-UI.md), generalizado a columnas con alineación
// propia en vez de "nombre + montos". No usar librerías tipo html2canvas.
const FUENTE = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

export function construirCanvasTabla({ titulo, subtitulo, columnas, filas, pie }) {
  const fBase = `13px ${FUENTE}`;
  const fBold = `600 13px ${FUENTE}`;
  const fTitulo = `700 18px ${FUENTE}`;
  const fSub = `12px ${FUENTE}`;
  const fPie = `italic 11px ${FUENTE}`;
  const padX = 28;
  const padY = 24;
  const gap = 24;
  const rowH = 26;
  const headH = 30;

  const medidor = document.createElement('canvas').getContext('2d');
  const anchoCols = columnas.map((c, i) => {
    medidor.font = fBold;
    let w = medidor.measureText(c.label).width;
    medidor.font = fBase;
    for (const f of filas) w = Math.max(w, medidor.measureText(f[i] ?? '').width);
    return Math.ceil(w) + 4;
  });

  const contentW = anchoCols.reduce((a, b) => a + b, 0) + gap * (columnas.length - 1);
  const width = Math.max(320, padX * 2 + contentW);
  const alto = padY + (24 + (subtitulo ? 18 + 6 : 0)) + headH + rowH * filas.length + 1 + (pie ? 12 + 14 : 0) + padY;

  const dpr = 2;
  const canvas = document.createElement('canvas');
  canvas.width = width * dpr;
  canvas.height = alto * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  ctx.textBaseline = 'middle';

  const r = 12;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.arcTo(width, 0, width, alto, r);
  ctx.arcTo(width, alto, 0, alto, r);
  ctx.arcTo(0, alto, 0, 0, r);
  ctx.arcTo(0, 0, width, 0, r);
  ctx.closePath();
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1;
  ctx.stroke();

  // Posición X de cada columna: izquierda del área, o derecha para alinear a la derecha.
  const xIzq = [];
  const xDer = [];
  let acum = padX;
  columnas.forEach((c, i) => {
    xIzq[i] = acum;
    acum += anchoCols[i];
    xDer[i] = acum;
    acum += gap;
  });

  let y = padY;
  ctx.fillStyle = '#0f172a';
  ctx.font = fTitulo;
  ctx.textAlign = 'left';
  ctx.fillText(titulo, padX, y + 10);
  y += 24;
  if (subtitulo) {
    ctx.fillStyle = '#94a3b8';
    ctx.font = fSub;
    ctx.fillText(subtitulo, padX, y + 9);
    y += 18 + 6;
  }

  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(padX - 8, y, width - (padX - 8) * 2, headH);
  ctx.fillStyle = '#64748b';
  ctx.font = fBold;
  columnas.forEach((c, i) => {
    ctx.textAlign = c.align === 'right' ? 'right' : 'left';
    ctx.fillText(c.label, c.align === 'right' ? xDer[i] : xIzq[i], y + headH / 2 + 1);
  });
  y += headH;

  filas.forEach((f) => {
    ctx.strokeStyle = '#f1f5f9';
    ctx.beginPath();
    ctx.moveTo(padX - 8, y);
    ctx.lineTo(width - (padX - 8), y);
    ctx.stroke();
    ctx.font = fBase;
    ctx.fillStyle = '#334155';
    columnas.forEach((c, i) => {
      ctx.textAlign = c.align === 'right' ? 'right' : 'left';
      ctx.fillText(f[i] ?? '', c.align === 'right' ? xDer[i] : xIzq[i], y + rowH / 2 + 1);
    });
    y += rowH;
  });

  if (pie) {
    y += 12;
    ctx.font = fPie;
    ctx.fillStyle = '#94a3b8';
    ctx.textAlign = 'left';
    ctx.fillText(pie, padX, y + 6);
  }

  return canvas;
}

export function canvasABlob(canvas) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

export function descargarBlobImagen(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Copia la imagen al portapapeles; si el navegador no lo permite, la
// descarga y avisa (igual que ListaPrecios.jsx).
export async function copiarOCaerADescarga(canvas, nombreArchivo) {
  const blob = await canvasABlob(canvas);
  if (navigator.clipboard && window.ClipboardItem) {
    await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
    return true;
  }
  descargarBlobImagen(blob, nombreArchivo);
  return false;
}
